import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import CartSidebar from '../../components/feature/CartSidebar';
import SearchModal from '../../components/feature/SearchModal';
import LoginModal from '../../components/feature/LoginModal';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { SITE_ID } from '../../lib/constants';
import { useIller, useIlceler } from '../../hooks/useIlIlce';

interface Address {
  id: string;
  ad_soyad: string;
  telefon: string;
  adres_basligi: string;
  adres: string;
  il: string;
  ilce: string;
  varsayilan: boolean;
}

const emptyForm = {
  ad_soyad: '',
  telefon: '',
  adres_basligi: '',
  adres: '',
  il: '',
  ilce: '',
  varsayilan: false,
};

export default function AddressesPage() {
  const [searchOpen, setSearchOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [formError, setFormError] = useState('');
  const [telefonError, setTelefonError] = useState('');
  const { user } = useAuth();
  const { iller } = useIller();
  const { ilceler } = useIlceler(form.il);

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    fetchAddresses();
  }, [user]);

  async function fetchAddresses() {
    setLoading(true);
    const { data } = await supabase
      .from('kullanici_adresleri')
      .select('*')
      .eq('site_id', SITE_ID)
      .eq('user_id', user!.id)
      .order('varsayilan', { ascending: false });
    setAddresses((data as Address[]) || []);
    setLoading(false);
  }

  function openAddForm() {
    setEditingId(null);
    setForm(emptyForm);
    setFormError('');
    setShowForm(true);
  }

  function openEditForm(addr: Address) {
    setEditingId(addr.id);
    // il alanı DB'de text (il_adi) olarak tutuluyor, dropdown için il_kodu'na çevir
    const ilKodu = iller.find(i => i.il_adi === addr.il)?.il_kodu || addr.il;
    setForm({
      ad_soyad: addr.ad_soyad,
      telefon: addr.telefon,
      adres_basligi: addr.adres_basligi,
      adres: addr.adres,
      il: ilKodu,
      ilce: addr.ilce,
      varsayilan: addr.varsayilan,
    });
    setFormError('');
    setTelefonError('');
    setShowForm(true);
  }

  async function handleSave() {
    setFormError('');
    setTelefonError('');

    const digits = form.telefon.replace(/\D/g, '');
    if (!form.ad_soyad || !form.telefon || !form.adres || !form.il || !form.ilce) {
      setFormError('Lütfen zorunlu alanları doldurun.');
      return;
    }
    if (digits.length !== 10 || !digits.startsWith('5')) {
      setTelefonError('Geçerli bir telefon numarası girin (5XX XXX XX XX)');
      return;
    }

    setSaving(true);

    // il alanına il_kodu değil il_adi kaydet
    const ilAdi = iller.find(i => i.il_kodu === form.il)?.il_adi || form.il;

    const payload = {
      ad_soyad: form.ad_soyad,
      telefon: form.telefon,
      adres_basligi: form.adres_basligi || 'Adresim',
      adres: form.adres,
      il: ilAdi,
      ilce: form.ilce,
      varsayilan: form.varsayilan,
      site_id: SITE_ID,
      user_id: user!.id,
    };

    if (editingId) {
      await supabase.from('kullanici_adresleri').update(payload).eq('id', editingId);
    } else {
      await supabase.from('kullanici_adresleri').insert(payload);
    }

    await fetchAddresses();
    setSaving(false);
    setShowForm(false);
  }

  async function handleDelete(id: string) {
    setDeletingId(id);
    await supabase.from('kullanici_adresleri').delete().eq('id', id);
    setAddresses(prev => prev.filter(a => a.id !== id));
    setDeletingId(null);
  }

  async function setDefault(id: string) {
    await supabase
      .from('kullanici_adresleri')
      .update({ varsayilan: false })
      .eq('site_id', SITE_ID)
      .eq('user_id', user!.id);
    await supabase
      .from('kullanici_adresleri')
      .update({ varsayilan: true })
      .eq('id', id);
    await fetchAddresses();
  }

  return (
    <div className="min-h-screen bg-cream-light font-sans">
      <Navbar onSearchOpen={() => setSearchOpen(true)} onLoginOpen={() => setLoginOpen(true)} />

      <main className="pt-24 pb-20">
        <div className="max-w-3xl mx-auto px-4 lg:px-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <Link to="/profil" className="w-9 h-9 flex items-center justify-center rounded-xl bg-white hover:bg-cream transition-colors cursor-pointer">
                <i className="ri-arrow-left-line text-coffee" />
              </Link>
              <div>
                <h1 className="font-serif text-2xl font-bold text-coffee">Adreslerim</h1>
                <p className="text-xs text-coffee-light mt-0.5">Kayıtlı teslimat adreslerim</p>
              </div>
            </div>
            {user && !showForm && (
              <button
                onClick={openAddForm}
                className="flex items-center gap-2 px-4 py-2 bg-coffee text-white text-sm font-semibold rounded-xl hover:bg-coffee-medium transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-add-line" />
                Yeni Adres
              </button>
            )}
          </div>

          {!user ? (
            <div className="bg-white rounded-3xl p-12 text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-cream rounded-full mx-auto mb-4">
                <i className="ri-map-pin-line text-2xl text-coffee-light" />
              </div>
              <p className="text-coffee-light mb-6 text-sm">Adreslerini görmek için giriş yapman gerekiyor.</p>
              <button
                onClick={() => setLoginOpen(true)}
                className="px-6 py-3 bg-coffee text-white font-semibold rounded-xl hover:bg-coffee-medium transition-colors whitespace-nowrap cursor-pointer"
              >
                Giriş Yap
              </button>
            </div>
          ) : showForm ? (
            /* Address Form */
            <div className="bg-white rounded-3xl p-6 lg:p-8">
              <h2 className="font-serif text-lg font-bold text-coffee mb-6">
                {editingId ? 'Adresi Düzenle' : 'Yeni Adres Ekle'}
              </h2>
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-coffee-medium mb-1.5">Ad Soyad *</label>
                    <input
                      type="text"
                      value={form.ad_soyad}
                      onChange={e => setForm(f => ({ ...f, ad_soyad: e.target.value }))}
                      className="w-full px-4 py-3 text-sm bg-cream rounded-xl border border-transparent focus:border-gold focus:outline-none focus:bg-white transition-all text-coffee"
                      placeholder="Ad Soyad"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-coffee-medium mb-1.5">Telefon *</label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm text-coffee-medium select-none pointer-events-none">+90</span>
                      <input
                        type="tel"
                        value={form.telefon}
                        onChange={e => {
                          setTelefonError('');
                          const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
                          let formatted = digits;
                          if (digits.length > 3 && digits.length <= 6) {
                            formatted = `${digits.slice(0, 3)} ${digits.slice(3)}`;
                          } else if (digits.length > 6 && digits.length <= 8) {
                            formatted = `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
                          } else if (digits.length > 8) {
                            formatted = `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 8)} ${digits.slice(8)}`;
                          }
                          setForm(f => ({ ...f, telefon: formatted }));
                        }}
                        maxLength={13}
                        className={`w-full pl-12 pr-4 py-3 text-sm bg-cream rounded-xl border ${telefonError ? 'border-red-400' : 'border-transparent'} focus:border-gold focus:outline-none focus:bg-white transition-all text-coffee`}
                        placeholder="5XX XXX XX XX"
                      />
                    </div>
                    {telefonError && <p className="text-xs text-red-500 mt-1">{telefonError}</p>}
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-coffee-medium mb-1.5">Adres Başlığı</label>
                  <input
                    type="text"
                    value={form.adres_basligi}
                    onChange={e => setForm(f => ({ ...f, adres_basligi: e.target.value }))}
                    className="w-full px-4 py-3 text-sm bg-cream rounded-xl border border-transparent focus:border-gold focus:outline-none focus:bg-white transition-all text-coffee"
                    placeholder="Ev, İş, vb."
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-coffee-medium mb-1.5">Adres *</label>
                  <textarea
                    value={form.adres}
                    onChange={e => setForm(f => ({ ...f, adres: e.target.value }))}
                    rows={3}
                    className="w-full px-4 py-3 text-sm bg-cream rounded-xl border border-transparent focus:border-gold focus:outline-none focus:bg-white transition-all text-coffee resize-none"
                    placeholder="Mahalle, sokak, bina no, daire no..."
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-coffee-medium mb-1.5">İl *</label>
                    <select
                      value={form.il}
                      onChange={e => setForm(f => ({ ...f, il: e.target.value, ilce: '' }))}
                      className="w-full px-4 py-3 text-sm bg-cream rounded-xl border border-transparent focus:border-gold focus:outline-none focus:bg-white transition-all text-coffee cursor-pointer"
                    >
                      <option value="">İl seçin</option>
                      {iller.map(il => (
                        <option key={il.il_kodu} value={il.il_kodu}>{il.il_adi}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-coffee-medium mb-1.5">İlçe *</label>
                    <select
                      value={form.ilce}
                      onChange={e => setForm(f => ({ ...f, ilce: e.target.value }))}
                      disabled={!form.il}
                      className="w-full px-4 py-3 text-sm bg-cream rounded-xl border border-transparent focus:border-gold focus:outline-none focus:bg-white transition-all text-coffee cursor-pointer disabled:opacity-50"
                    >
                      <option value="">İlçe seçin</option>
                      {ilceler.map(ilce => (
                        <option key={ilce.id} value={ilce.ilce_adi}>{ilce.ilce_adi}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.varsayilan}
                      onChange={e => setForm(f => ({ ...f, varsayilan: e.target.checked }))}
                      className="w-4 h-4 accent-gold cursor-pointer"
                    />
                    <span className="text-sm text-coffee">Varsayılan adres olarak ayarla</span>
                  </label>
                </div>

                {formError && (
                  <div className="flex items-center gap-2 text-xs text-red-700 bg-red-50 px-3 py-2.5 rounded-xl">
                    <i className="ri-error-warning-line" />
                    {formError}
                  </div>
                )}

                <div className="flex gap-3 pt-2">
                  <button
                    onClick={() => setShowForm(false)}
                    className="flex-1 py-3 border-2 border-cream-dark text-coffee font-semibold rounded-xl hover:bg-cream transition-colors whitespace-nowrap cursor-pointer"
                  >
                    İptal
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="flex-1 py-3 bg-coffee text-white font-semibold rounded-xl hover:bg-coffee-medium transition-colors disabled:opacity-60 whitespace-nowrap cursor-pointer"
                  >
                    {saving ? (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-loader-4-line animate-spin" />
                        Kaydediliyor...
                      </span>
                    ) : editingId ? 'Güncelle' : 'Kaydet'}
                  </button>
                </div>
              </div>
            </div>
          ) : loading ? (
            <div className="space-y-3">
              {[1, 2].map(i => (
                <div key={i} className="bg-white rounded-2xl p-6 animate-pulse">
                  <div className="h-4 bg-cream rounded w-24 mb-3" />
                  <div className="h-3 bg-cream rounded w-48 mb-2" />
                  <div className="h-3 bg-cream rounded w-32" />
                </div>
              ))}
            </div>
          ) : addresses.length === 0 ? (
            <div className="bg-white rounded-3xl p-12 text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-cream rounded-full mx-auto mb-5">
                <i className="ri-map-pin-line text-3xl text-coffee-light" />
              </div>
              <h2 className="font-serif text-xl font-bold text-coffee mb-2">Kayıtlı adres yok</h2>
              <p className="text-coffee-light text-sm mb-6">Hızlı sipariş için adresini kaydet.</p>
              <button
                onClick={openAddForm}
                className="inline-flex items-center gap-2 px-6 py-3 bg-coffee text-white font-semibold rounded-xl hover:bg-coffee-medium transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-add-line" />
                Adres Ekle
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {addresses.map(addr => (
                <div key={addr.id} className="bg-white rounded-2xl p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <div className="w-9 h-9 flex items-center justify-center bg-gold/10 rounded-xl flex-shrink-0 mt-0.5">
                        <i className="ri-map-pin-line text-gold" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <span className="font-semibold text-sm text-coffee">
                            {addr.adres_basligi || 'Adresim'}
                          </span>
                          {addr.varsayilan && (
                            <span className="text-xs bg-gold/10 text-gold px-2 py-0.5 rounded-full font-medium">
                              Varsayılan
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-coffee-light leading-relaxed">{addr.ad_soyad}</p>
                        <p className="text-sm text-coffee-light leading-relaxed">{addr.adres}</p>
                        <p className="text-sm text-coffee-light">
                          {addr.ilce} / {addr.il}
                        </p>
                        <p className="text-xs text-coffee-light mt-1">{addr.telefon}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-1 flex-shrink-0">
                      {!addr.varsayilan && (
                        <button
                          onClick={() => setDefault(addr.id)}
                          className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-cream transition-colors cursor-pointer"
                          title="Varsayılan yap"
                        >
                          <i className="ri-star-line text-coffee-light text-sm" />
                        </button>
                      )}
                      <button
                        onClick={() => openEditForm(addr)}
                        className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-cream transition-colors cursor-pointer"
                        title="Düzenle"
                      >
                        <i className="ri-edit-line text-coffee-light text-sm" />
                      </button>
                      <button
                        onClick={() => handleDelete(addr.id)}
                        disabled={deletingId === addr.id}
                        className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 transition-colors cursor-pointer"
                        title="Sil"
                      >
                        {deletingId === addr.id ? (
                          <i className="ri-loader-4-line animate-spin text-sm text-coffee-light" />
                        ) : (
                          <i className="ri-delete-bin-line text-red-400 text-sm" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      <Footer />
      <CartSidebar />
      <SearchModal isOpen={searchOpen} onClose={() => setSearchOpen(false)} />
      <LoginModal isOpen={loginOpen} onClose={() => setLoginOpen(false)} initialMode="login" />
    </div>
  );
}
